import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Store } from '../../Store';
import PageHeader from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import ConfirmModal from '../../components/ConfirmModal';
import Pagination from '../../components/Pagination/Pagination';
import { getError } from '../../utils';
import userService from '../../services/user.service';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        users: action.payload,
        loading: false,
        itemQuantity: action.payload.length,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'DELETE_REQUEST':
      return { ...state, loadingDelete: true, successDelete: false };
    case 'DELETE_SUCCESS':
      return { ...state, loadingDelete: false, successDelete: true };
    case 'DELETE_FAIL':
      return { ...state, loadingDelete: false, successDelete: false };
    case 'DELETE_RESET':
      return { ...state, loadingDelete: false, successDelete: false };
    default:
      return state;
  }
};

export default function UserList() {
  const { t } = useTranslation();
  const [{ loading, error, users, successDelete, itemQuantity }, dispatch] = useReducer(reducer, {
    users: [],
    loading: true,
    error: '',
    itemQuantity: 0,
  });

  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const onPageChange = async pageNumber => {
    setCurrentPage(pageNumber);
    await fetchData((pageNumber - 1) * itemsPerPage);
  };

  const fetchData = async skip => {
    try {
      dispatch({ type: 'FETCH_REQUEST' });
      const data = await userService.getUsers(skip, itemsPerPage, userInfo.token);
      dispatch({ type: 'FETCH_SUCCESS', payload: data });
    } catch (err) {
      dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
    }
  };

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: 'DELETE_RESET' });
    } else {
      fetchData(0);
    }
  }, [successDelete]);

  const handleDeleteClick = user => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const deleteUserHandler = async () => {
    try {
      dispatch({ type: 'DELETE_REQUEST' });
      await userService.deleteUser(selectedUser._id, userInfo.token);
      dispatch({ type: 'DELETE_SUCCESS' });
      toast.success(t('common.success'));
      handleCloseDeleteModal();
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'DELETE_FAIL' });
    }
  };

  return (
    <div className='space-y-6'>
      <ConfirmModal
        show={showDeleteModal}
        title={t('common.delete')}
        message={t('common.confirmDelete')}
        onConfirm={deleteUserHandler}
        onCancel={handleCloseDeleteModal}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        destructive
      />

      <PageHeader
        title={t('admin.adminList')}
        subtitle='Administrá los usuarios con acceso al sistema'
        actions={
          <Button onClick={() => navigate('/AdminScreen/newUser')}>
            <Plus className='h-4 w-4' />
            {t('admin.newAdmin')}
          </Button>
        }
      />

      <Card>
        <CardContent className='p-0'>
          {loading ? (
            <div className='divide-y divide-border'>
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className='flex items-center gap-4 px-6 py-4'>
                  <div className='h-3 w-1/4 animate-pulse rounded bg-muted' />
                  <div className='h-3 w-1/4 animate-pulse rounded bg-muted' />
                  <div className='h-3 w-1/6 animate-pulse rounded bg-muted' />
                </div>
              ))}
            </div>
          ) : error ? (
            <p className='px-6 py-5 text-sm text-destructive'>{error}</p>
          ) : !users || users.length === 0 ? (
            <div className='px-6 py-12 text-center text-sm text-muted-foreground'>
              <p>No hay usuarios todavía.</p>
              <div className='mt-3'>
                <Button size='sm' onClick={() => navigate('/AdminScreen/newUser')}>
                  Crear el primer usuario
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('auth.name')}</TableHead>
                  <TableHead>{t('auth.email')}</TableHead>
                  <TableHead>{t('admin.description')}</TableHead>
                  <TableHead className='w-[120px]' />
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user._id}>
                    <TableCell className='font-medium'>{user.nombre || '—'}</TableCell>
                    <TableCell className='text-muted-foreground'>{user.email || '—'}</TableCell>
                    <TableCell className='text-muted-foreground'>
                      {user.descripcion || '—'}
                    </TableCell>
                    <TableCell>
                      <div className='flex items-center justify-end gap-1'>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('common.viewDetails')}
                          className='text-muted-foreground hover:text-foreground'
                          onClick={() => navigate(`/AdminScreen/admin/${user._id}`)}
                        >
                          <Eye className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('admin.edit')}
                          className='text-muted-foreground hover:text-foreground'
                          onClick={() => navigate(`/AdminScreen/editUser/${user._id}`)}
                        >
                          <Pencil className='h-4 w-4' />
                        </Button>
                        <Button
                          variant='ghost'
                          size='icon-sm'
                          title={t('common.delete')}
                          className='text-destructive hover:text-destructive'
                          onClick={() => handleDeleteClick(user)}
                        >
                          <Trash2 className='h-4 w-4' />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {itemQuantity > 0 && (
        <Pagination
          className='pagination-bar'
          totalCount={itemQuantity}
          onPageChange={onPageChange}
          currentPage={currentPage}
          pageSize={itemsPerPage}
        />
      )}
    </div>
  );
}
